import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import type { Dealer, CreateDealerInput } from '../../../api/dealers.api';

interface Props {
  open: boolean;
  initial?: Dealer | null;
  onClose: () => void;
  onSubmit: (input: CreateDealerInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateDealerInput = { dealerName: '', dealerType: 'retailer' };

const DEALER_TYPES = [
  { value: 'distributor', label: 'Distributor' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'oem_partner', label: 'OEM Partner' },
];

export function DealerFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateDealerInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? {
            dealerName: initial.dealerName,
            legalName: initial.legalName ?? undefined,
            dealerType: initial.dealerType,
            gstNumber: initial.gstNumber ?? undefined,
            panNumber: initial.panNumber ?? undefined,
            contactPerson: initial.contactPerson ?? undefined,
            email: initial.email ?? undefined,
            mobile: initial.mobile ?? undefined,
            regionId: initial.regionId,
            stateId: initial.stateId,
            creditLimit: initial.creditLimit,
            creditDays: initial.creditDays,
            bankAccountName: initial.bankAccountName ?? undefined,
            bankAccountNumber: initial.bankAccountNumber ?? undefined,
            bankIfsc: initial.bankIfsc ?? undefined,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateDealerInput>(k: K, v: CreateDealerInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v: string) => (v ? Number(v) : undefined);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Dealer — ${initial?.dealerCode}` : 'Register Dealer'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Business Vertical" value="Protection Device Installation" fullWidth disabled helperText="Dealers are mapped to the PDI vertical." />
          <TextField label="Dealer Name" value={form.dealerName} onChange={(e) => set('dealerName', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Legal Name" value={form.legalName ?? ''} onChange={(e) => set('legalName', e.target.value)} fullWidth />
            <TextField select label="Dealer Type" value={form.dealerType ?? 'retailer'} onChange={(e) => set('dealerType', e.target.value)} fullWidth>
              {DEALER_TYPES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="GST Number" value={form.gstNumber ?? ''} onChange={(e) => set('gstNumber', e.target.value.toUpperCase())} fullWidth />
            <TextField label="PAN" value={form.panNumber ?? ''} onChange={(e) => set('panNumber', e.target.value.toUpperCase())} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Contact Person" value={form.contactPerson ?? ''} onChange={(e) => set('contactPerson', e.target.value)} fullWidth />
            <TextField label="Email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Mobile" value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} fullWidth />
            <TextField label="Region ID" type="number" value={form.regionId ?? ''} onChange={(e) => set('regionId', e.target.value ? Number(e.target.value) : null)} fullWidth />
            <TextField label="State ID" type="number" value={form.stateId ?? ''} onChange={(e) => set('stateId', e.target.value ? Number(e.target.value) : null)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Credit Limit (₹)" type="number" value={form.creditLimit ?? ''} onChange={(e) => set('creditLimit', num(e.target.value))} fullWidth />
            <TextField label="Credit Days" type="number" value={form.creditDays ?? ''} onChange={(e) => set('creditDays', num(e.target.value))} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Bank A/C Name" value={form.bankAccountName ?? ''} onChange={(e) => set('bankAccountName', e.target.value)} fullWidth />
            <TextField label="Bank A/C No." value={form.bankAccountNumber ?? ''} onChange={(e) => set('bankAccountNumber', e.target.value)} fullWidth />
            <TextField label="IFSC" value={form.bankIfsc ?? ''} onChange={(e) => set('bankIfsc', e.target.value.toUpperCase())} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Register'}</Button>
      </DialogActions>
    </Dialog>
  );
}
